import { useState, useEffect } from 'react';
import { Shield, Settings } from 'lucide-react';
import api, { type QualityGateSettings } from '../api';

interface Props {
  projectId: string;
}

export default function QualityGateSettingsPanel({ projectId }: Props) {
  const [settings, setSettings] = useState<QualityGateSettings | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [threshold, setThreshold] = useState(60);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.qualityGate(projectId)
      .then((s: QualityGateSettings) => {
        setSettings(s);
        setEnabled(s.issue_quality_gate_enabled);
        setThreshold(s.issue_quality_threshold);
      })
      .catch(() => {});
  }, [projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.qualityGateUpdate(projectId, {
        issue_quality_gate_enabled: enabled,
        issue_quality_threshold: threshold,
      });
      const updated = await api.qualityGate(projectId);
      setSettings(updated);
    } catch { /* non-fatal */ }
    finally { setSaving(false); }
  };

  const thresholdLabel = threshold <= 40 ? 'Loose' : threshold <= 70 ? 'Default' : 'Strict';

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 16 }}>
      <div style={{
        padding: '14px 18px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Shield size={14} style={{ color: 'var(--blue)' }} />
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
          Issue Quality Gate
        </span>
      </div>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--blue)' }}
          />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
            Enable issue quality gate
          </span>
        </label>

        {/* Threshold slider */}
        {enabled && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)' }}>
                Minimum quality score
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-sm)',
                color: 'var(--text)',
              }}>
                {threshold}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                ({thresholdLabel})
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--blue)' }}
            />
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 10, color: 'var(--text-muted)', marginTop: 2,
            }}>
              <span>0 (off)</span>
              <span>40 (loose)</span>
              <span>60 (default)</span>
              <span>80 (strict)</span>
              <span>100</span>
            </div>
          </div>
        )}

        {/* Help text */}
        <div style={{
          fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.5,
          padding: '6px 10px',
          background: 'rgba(59, 130, 246, 0.04)',
          borderRadius: 'var(--radius-sm)',
        }}>
          Issues below this score will prompt for clarification before burning pipeline credits.
          Typical thresholds: 40 (loose), 60 (default), 80 (strict).
        </div>

        {/* Recent scores analytics */}
        {settings && settings.recent_score_count > 0 && (
          <div style={{
            fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Settings size={11} style={{ color: 'var(--text-muted)' }} />
            Last {settings.recent_score_count} issues averaged: <strong>{settings.recent_avg_score}/100</strong>
            {settings.recent_override_count > 0 && (
              <span style={{ color: 'var(--orange)' }}>
                ({settings.recent_override_count} override{settings.recent_override_count > 1 ? 's' : ''})
              </span>
            )}
          </div>
        )}

        {/* Save */}
        <button
          className="btn btn-sm"
          disabled={saving}
          onClick={handleSave}
          style={{
            alignSelf: 'flex-start',
            background: 'var(--blue)', color: '#fff', border: 'none',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Quality Gate Settings'}
        </button>
      </div>
    </div>
  );
}
